import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Calendar, Home, DollarSign, Building, HelpCircle, Heart, Sparkles, Moon, Leaf } from 'lucide-react';

const ctaButtons = [
  {
    icon: Calendar,
    text: "Book & Schedule",
    onClick: () => window.open('https://calendly.com/udaadu', '_blank')
  },
  {
    icon: Building,
    text: "What Fits My Lot?",
    onClick: () => window.open('/landing', '_self')
  },
  {
    icon: DollarSign,
    text: "Get Price & ROI Report",
    onClick: () => window.open('/adu-calculator', '_self')
  },
  {
    icon: Home,
    text: "Our Portfolio",
    onClick: () => window.open('/our-work', '_self')
  },
  {
    icon: HelpCircle,
    text: "Our FAQ",
    onClick: () => window.open('/faq', '_self')
  }
];

const HowToUseADUAsSoundHealingOrEnergyTherapySpace = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-sound-healing-or-energy-therapy-space" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Energy Healing in a Sacred Backyard Space
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Create a deeply personal, high-vibration environment for Reiki, sound baths, chakra work, or intuitive readings using your ADU as a healing studio.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Features Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>Ideal Features for Spiritual Practices</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Leaf className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Soothing textures: wood, stone, organic cotton</span>
                </div>
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Sound system or crystal bowl display</span>
                </div>
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Soft lighting (salt lamps, dimmable sconces)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Scent diffuser + sacred object shelves</span>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <img 
                src="https://images.unsplash.com/photo-1473177104440-ffee2f376098?auto=format&fit=crop&w=800&h=400" 
                alt="Sound Healing Energy Therapy ADU" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Layout Zones Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Suggested Layout Zones</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Treatment Areas</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Client mat or treatment table</li>
                <li>• Seated consultation or journaling corner</li>
                <li>• Breathwork/meditation nook</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Sacred Storage</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Storage cabinet for energy tools</li>
                <li>• Display shelves for crystals and bowls</li>
                <li>• Essential oil and incense storage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Wellness ROI Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>Wellness ROI</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Heart className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Full Client Privacy</h3>
              <p className="text-muted-foreground">Create a sacred container for healing work</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Home className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Retreat-Ready Resale</h3>
              <p className="text-muted-foreground">Unique wellness feature adds property value</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Sparkles className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Grounded Energy Work</h3>
              <p className="text-muted-foreground">Safe, grounded environment in your flow</p>
            </div>
          </div>
        </div>
      </section>

      {/* Healing Modalities */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>Perfect for Various Healing Modalities</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Heart className="w-6 h-6" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="font-semibold" style={{ color: '#E4683B' }}>Reiki</h3>
              <p className="text-sm text-muted-foreground">Energy healing sessions</p>
            </div>
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Sparkles className="w-6 h-6" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="font-semibold" style={{ color: '#E4683B' }}>Sound Baths</h3>
              <p className="text-sm text-muted-foreground">Crystal bowl healing</p>
            </div>
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Moon className="w-6 h-6" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="font-semibold" style={{ color: '#E4683B' }}>Chakra Work</h3>
              <p className="text-sm text-muted-foreground">Energy alignment</p>
            </div>
            <div className="text-center space-y-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Leaf className="w-6 h-6" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="font-semibold" style={{ color: '#E4683B' }}>Intuitive Readings</h3>
              <p className="text-sm text-muted-foreground">Spiritual guidance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Create Your Sacred Healing Sanctuary
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Design a high-vibration space where healing energy flows freely and transformation happens.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl w-full h-[600px] p-0">
          <div className="w-full h-full">
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUAsSoundHealingOrEnergyTherapySpace;